import { FC, useState } from 'react';
import { useLocale } from '@components/Contexts/LocaleContext';
import { ConsentChoice, consentText } from './consent';

interface Props {
  initial: ConsentChoice;
  onClose: () => void;
  onAcceptAll: () => void;
  onAcceptSelected: (choice: ConsentChoice) => void;
}

interface CardProps {
  title: string;
  description: string;
  alwaysActive?: string;
  checked?: boolean;
  onToggle?: (checked: boolean) => void;
}

const PreferenceCard: FC<CardProps> = ({
  title,
  description,
  alwaysActive,
  checked,
  onToggle,
}) => (
  <div className="border border-b-0 border-[#d2d2d2] px-5 py-4 first:rounded-t-lg last:rounded-b-lg last:border-b">
    <div className="mb-4 flex items-center justify-between">
      <span className="font-semibold">{title}</span>
      {alwaysActive ? (
        <span className="text-[blue] underline">{alwaysActive}</span>
      ) : (
        <label className="relative inline-block h-5 w-10 cursor-pointer">
          <input
            type="checkbox"
            aria-label={title}
            checked={checked}
            onChange={(e) => onToggle?.(e.target.checked)}
            className="peer absolute h-0 w-0 opacity-0"
          />
          <span className="absolute inset-0 rounded-full bg-[darkgray] transition-colors duration-150 ease-in peer-checked:bg-[dodgerblue]" />
          <span className="absolute left-1 top-[3px] h-3.5 w-3.5 rounded-full bg-white transition-[left] duration-150 ease-in peer-checked:left-[22px]" />
        </label>
      )}
    </div>
    <span className="text-[#6c757d]">{description}</span>
  </div>
);

// Same look as the old banner's "Privacy Preference" modal: a centered card
// with one bordered row per category and the two accept buttons.
const CookiePreferences: FC<Props> = ({
  initial,
  onClose,
  onAcceptAll,
  onAcceptSelected,
}) => {
  const { lang } = useLocale();
  const text = consentText[lang];
  const [analytics, setAnalytics] = useState(initial.analytics);
  const [marketing, setMarketing] = useState(initial.marketing);

  const buttonClass =
    'inline-flex h-[35px] w-[15%] min-w-[108px] items-center justify-center whitespace-pre rounded-md bg-[#09071f] px-1.5 font-semibold text-white transition-colors';

  return (
    <section
      role="dialog"
      aria-modal="true"
      aria-label={text.preferenceTitle}
      className="fixed left-1/2 top-1/2 z-[999999] w-[calc(100%-40px)] max-w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-[#e8e8e8] bg-white p-2.5 text-sm text-black shadow-[0_2px_8px_0_rgba(0,0,0,0.05)] md:min-w-[600px]"
    >
      <div className="mb-4 flex items-center justify-between border-b border-[#d2d2d2] px-1 pb-2.5 md:justify-start">
        <h2 className="m-0 text-xl font-bold md:mr-9">{text.preferenceTitle}</h2>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="ml-auto cursor-pointer"
        >
          &#10006;
        </button>
      </div>

      <div className="max-h-[60vh] overflow-y-auto p-4">
        <PreferenceCard
          title={text.necessary.name}
          description={text.necessary.description}
          alwaysActive={text.alwaysActive}
        />
        <PreferenceCard
          title={text.analytics.name}
          description={text.analytics.description}
          checked={analytics}
          onToggle={setAnalytics}
        />
        <PreferenceCard
          title={text.marketing.name}
          description={text.marketing.description}
          checked={marketing}
          onToggle={setMarketing}
        />
      </div>

      <div className="flex items-center justify-end px-4 pb-4">
        <button type="button" onClick={onAcceptAll} className={`${buttonClass} mr-2.5`}>
          {text.acceptAll}
        </button>
        <button
          type="button"
          onClick={() => onAcceptSelected({ analytics, marketing })}
          className={buttonClass}
        >
          {text.acceptSelected}
        </button>
      </div>
    </section>
  );
};

export default CookiePreferences;
